<template>
  <v-card color="grey lighten-4" class="mb-8">
    <v-card-title
      class="text-uppercase font-weight-bold grey--text text--darken-3"
    >
      <v-spacer></v-spacer>
      <v-btn rounded color="secondary" @click="exportToExcel">
        <v-icon>mdi-microsoft-excel</v-icon>
        {{ $t("download_excel") }}
      </v-btn>
    </v-card-title>
    <v-card-text class="white">
      <v-data-table
        :headers="headers"
        :items="tableData"
        hide-default-footer
        disable-pagination
      >
        <!-- Columna de Fecha -->
        <template v-slot:[`item.date`]="{ item }">
          <div class="font-weight-bold text-capitalize">
            {{ getDate(item.date) }}
          </div>
          <div class="caption grey--text">vs {{ vsLabel }}</div>
        </template>

        <template v-slot:[`item.total_sale`]="{ item }">
          <a-kpi-columns
            kpi="total_sale"
            :value="item.total_sale.value"
            :vs_values="[item.total_sale.vs]"
            :vs_periods="getKpiPeriods"
            x_small
          ></a-kpi-columns>
        </template>

        <template v-slot:[`item.total_customers`]="{ item }">
          <a-kpi-columns
            kpi="total_customers"
            :value="item.total_customers.value"
            :vs_values="[item.total_customers.vs]"
            :vs_periods="getKpiPeriods"
            x_small
          ></a-kpi-columns>
        </template>

        <template v-slot:[`item.avg_customers`]="{ item }">
          <a-kpi-columns
            kpi="avg_customers"
            :value="item.avg_customers.value"
            :vs_values="[item.avg_customers.vs]"
            :vs_periods="getKpiPeriods"
            x_small
          ></a-kpi-columns>
        </template>

        <template v-slot:[`item.total_tickets`]="{ item }">
          <a-kpi-columns
            kpi="total_tickets"
            :value="item.total_tickets.value"
            :vs_values="[item.total_tickets.vs]"
            :vs_periods="getKpiPeriods"
            x_small
          ></a-kpi-columns>
        </template>

        <template v-slot:[`item.avg_ticket`]="{ item }">
          <a-kpi-columns
            kpi="avg_ticket"
            :value="item.avg_ticket.value"
            :vs_values="[item.avg_ticket.vs]"
            :vs_periods="getKpiPeriods"
            x_small
          ></a-kpi-columns>
        </template>

        <template
          v-slot:[`item.revenuerestaurant_forecast_other_cost_month_avg`]="{
            item
          }"
        >
          <a-kpi-columns
            kpi="revenuerestaurant_forecast_other_cost_month_avg"
            :value="item.revenuerestaurant_forecast_other_cost_month_avg.value"
            :vs_values="[
              item.revenuerestaurant_forecast_other_cost_month_avg.vs
            ]"
            :vs_periods="getKpiPeriods"
            x_small
          ></a-kpi-columns>
        </template>

        <template
          v-slot:[`item.revenuerestaurant_forecast_sales_cost_month_avg`]="{
            item
          }"
        >
          <a-kpi-columns
            kpi="revenuerestaurant_forecast_sales_cost_month_avg"
            :value="item.revenuerestaurant_forecast_sales_cost_month_avg.value"
            :vs_values="[
              item.revenuerestaurant_forecast_sales_cost_month_avg.vs
            ]"
            :vs_periods="getKpiPeriods"
            x_small
          ></a-kpi-columns>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import aKpiColumns from "@/components/rms_ui/aKpiColumns.vue";
import Vue from "vue";
import { mapState } from "vuex";
import * as XLSX from "xlsx";

// Función auxiliar para convertir string a ArrayBuffer
function s2ab(s) {
  const buf = new ArrayBuffer(s.length);
  const view = new Uint8Array(buf);
  for (let i = 0; i < s.length; i++) {
    view[i] = s.charCodeAt(i) & 0xff;
  }
  return buf;
}

export default {
  name: "a-restaurant-sales-table",
  components: {
    aKpiColumns
  },
  computed: {
    vsLabel() {
      let vsText = "";
      if (this.sales_vs === "presupuesto") {
        vsText += " " + this.$t("bud");
      } else if (this.sales_vs === "ano_pasado") {
        vsText += " " + this.$t("ly");
      } else if (this.sales_vs === "otro_hotel") {
        let hotelData = this.$store.state.hotel.hotels.find(
          h => h.id === this.sales_hotel_vs
        );
        vsText += " " + hotelData.name;
      }
      return vsText;
    },
    mode() {
      if (this.sales_period === "current_month") {
        return "daily";
      } else if (this.sales_period === "current_year") {
        return "monthly";
      } else if (this.sales_period === "past") {
        if (this.sales_period_month === 0 && this.sales_period_year > 0) {
          return "monthly";
        } else if (this.sales_period_month > 0 && this.sales_period_year > 0) {
          return "daily";
        }
      }
      return "monthly";
    },
    getKpiPeriods() {
      let periods = [];
      if (this.sales_vs === "presupuesto") {
        periods.push("bud");
      } else if (this.sales_vs === "ano_pasado") {
        periods.push("ly");
      } else if (this.sales_vs === "otro_hotel") {
        periods.push("otb");
      }
      return periods;
    },
    headers() {
      return [
        {
          text: this.$t("date"),
          value: "date",
          align: "left",
          sortable: true
        },
        {
          text: this.$t("total_sale"),
          value: "total_sale",
          align: "center",
          sortable: true
        },
        {
          text: this.$t("diners"),
          value: "total_customers",
          align: "center",
          sortable: true
        },
        {
          text: this.$t("diners_per_ticket"),
          value: "avg_customers",
          align: "center",
          sortable: true
        },
        {
          text: this.$t("tickets"),
          value: "total_tickets",
          align: "center",
          sortable: true
        },
        {
          text: this.$t("average_ticket"),
          value: "avg_ticket",
          align: "center",
          sortable: true
        },
        {
          text: this.$t("purchases_ratio"),
          value: "revenuerestaurant_forecast_other_cost_month_avg",
          align: "center",
          sortable: true
        },
        {
          text: this.$t("other_expenses_ratio"),
          value: "revenuerestaurant_forecast_sales_cost_month_avg",
          align: "center",
          sortable: true
        }
      ];
    },
    tableData() {
      if (
        !this.sales_report_list ||
        !this.sales_report_list.daily_list ||
        !this.sales_report_list.daily_list_to_compare
      ) {
        return [];
      }

      return this.sales_report_list.daily_list.map((item, index) => {
        const compareItem =
          this.sales_report_list.daily_list_to_compare &&
          this.sales_report_list.daily_list_to_compare[index]
            ? this.sales_report_list.daily_list_to_compare[index]
            : null;

        return {
          date: item.date_original,
          total_sale: {
            value: item.total_sale.gross,
            vs:
              compareItem &&
              compareItem.total_sale &&
              compareItem.total_sale.gross
                ? compareItem.total_sale.gross
                : 0
          },
          total_customers: {
            value: item.total_customers,
            vs:
              compareItem && compareItem.total_customers
                ? compareItem.total_customers
                : 0
          },
          avg_customers: {
            value: item.avg_customers,
            vs:
              compareItem && compareItem.avg_customers
                ? compareItem.avg_customers
                : 0
          },
          total_tickets: {
            value: item.total_tickets,
            vs:
              compareItem &&
              compareItem.total_tickets &&
              compareItem.total_tickets.gross
                ? compareItem.total_tickets.gross
                : 0
          },
          avg_ticket: {
            value: item.avg_ticket.gross,
            vs:
              compareItem &&
              compareItem.avg_ticket &&
              compareItem.avg_ticket.gross
                ? compareItem.avg_ticket.gross
                : 0
          },
          revenuerestaurant_forecast_other_cost_month_avg: {
            value: item.revenuerestaurant_forecast_other_cost_month_avg,
            vs:
              compareItem &&
              compareItem.revenuerestaurant_forecast_other_cost_month_avg
                ? compareItem.revenuerestaurant_forecast_other_cost_month_avg
                : 0
          },
          revenuerestaurant_forecast_sales_cost_month_avg: {
            value: item.revenuerestaurant_forecast_sales_cost_month_avg,
            vs:
              compareItem &&
              compareItem.revenuerestaurant_forecast_sales_cost_month_avg
                ? compareItem.revenuerestaurant_forecast_sales_cost_month_avg
                : 0
          }
        };
      });
    },
    ...mapState({
      sales_period: state => state.restaurant.sales_period,
      sales_period_month: state => state.restaurant.sales_period_month,
      sales_period_year: state => state.restaurant.sales_period_year,
      sales_vs: state => state.restaurant.sales_vs,
      sales_hotel_vs: state => state.restaurant.sales_hotel_vs,
      sales_report_list: state => state.restaurant.sales_report_list
    })
  },
  methods: {
    getDate(date) {
      if (this.mode === "monthly") {
        return Vue.moment(date, "YYYYMMDD").format("MMMM YYYY");
      } else {
        return Vue.moment(date, "YYYYMMDD").format("dddd, DD MMMM YYYY");
      }
    },
    exportToExcel() {
      try {
        // Preparar los datos para la exportación
        const excelData = this.tableData.map(item => {
          // Convertir los datos al formato plano para Excel
          return {
            [this.$t("date")]: this.getDate(item.date),
            [this.$t("total_sale")]: item.total_sale.value + "€",
            [this.$t("diners")]: item.total_customers.value,
            [this.$t("diners_per_ticket")]: item.avg_customers.value,
            [this.$t("tickets")]: item.total_tickets.value,
            [this.$t("average_ticket")]: item.avg_ticket.value + "€",
            [this.$t("purchases_ratio")]:
              item.revenuerestaurant_forecast_other_cost_month_avg.value + "%",
            [this.$t("other_expenses_ratio")]:
              item.revenuerestaurant_forecast_sales_cost_month_avg.value + "%"
          };
        });

        // Crear una hoja de trabajo
        const worksheet = XLSX.utils.json_to_sheet(excelData);

        // Crear un libro de trabajo y añadir la hoja
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(
          workbook,
          worksheet,
          this.$t("sales_report")
        );

        // Generar y descargar el archivo Excel - Enfoque directo
        const filename = `${this.$t("sales_report")}.xlsx`;

        // Usar el método exportar a Excel con formato binario (más compatible)
        const wbout = XLSX.write(workbook, {
          bookType: "xlsx",
          type: "binary"
        });

        // Crear un blob con los datos binarios
        const blob = new Blob([s2ab(wbout)], {
          type: "application/octet-stream"
        });

        // Crear un objeto URL para el blob
        const url = window.URL.createObjectURL(blob);

        // Crear enlace y descargar
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();

        // Limpiar recursos
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        }, 0);

        this.$emit("export-excel");
      } catch (error) {
        // console.error("Error exportando a Excel:", error);
      }
    }
  }
};
</script>
